import type { Value, PointTarget, EdgeTarget, GeometryElementDisplayConfig } from '../../../geometrypad.model';

// =============================================================================
// TYPES
// =============================================================================

/** Parallelogram construction format (used for explicit format override; auto-detected when omitted) */
export type ParallelogramFormat = 'BSA';

export type ParallelogramProperty = 'b' | 's' | 'angle';
export type ParallelogramVertexName = 'A' | 'B' | 'C' | 'D';
export type ParallelogramEdgeName = 'bottom' | 'right' | 'top' | 'left';

export interface ParallelogramElement {
  type: 'Parallelogram';
  params: {
    b?: Value;
    s?: Value;
    angle?: Value;
    A?: PointTarget;
    B?: PointTarget;
    C?: PointTarget;
    D?: PointTarget;
    bottom?: EdgeTarget;
    right?: EdgeTarget;
    top?: EdgeTarget;
    left?: EdgeTarget;
  };
  names?: { b?: string; s?: string; angle?: string; h?: string; d1?: string; d2?: string };
}

// =============================================================================
// STRUCTURAL CONSTANTS
// =============================================================================

export const PARALLELOGRAM_EDGE_NAMES: readonly string[] = ['bottom', 'right', 'top', 'left'] as const;
export const PARALLELOGRAM_PROPERTY_FOR_EDGE: Record<string, 'b' | 's'> = {
  bottom: 'b',
  right: 's',
  top: 'b',
  left: 's',
};

// =============================================================================
// DISPLAY DEFAULTS
// =============================================================================

/**
 * Default display theme for parallelograms.
 *
 * Edge colors: bottom=sky, right=pink, top=purple, left=teal
 * Angle colors: alpha=sky, beta=pink, gamma=purple, delta=teal
 */
export const PARALLELOGRAM_DEFAULTS: GeometryElementDisplayConfig = {
  polygon: { fillColor: 'sky', fillOpacity: 0.1 },
  line: { strokeWidth: 2, color: 'neutral' },
  angle: { fillOpacity: 0.2, radius: 0.8 },
  point: { visible: false, size: 3 },
  subparts: {
    bottom: { line: { color: 'sky' } },
    right: { line: { color: 'pink' } },
    top: { line: { color: 'purple' } },
    left: { line: { color: 'teal' } },
    // Angles
    alpha: { angle: { fillColor: 'sky', strokeColor: 'sky', visible: true } },
    beta: { angle: { fillColor: 'pink', strokeColor: 'pink', visible: true } },
    gamma: { angle: { fillColor: 'purple', strokeColor: 'purple', visible: true } },
    delta: { angle: { fillColor: 'teal', strokeColor: 'teal', visible: true } },
    // Points
    A: { point: { color: 'sky' } },
    B: { point: { color: 'pink' } },
    C: { point: { color: 'purple' } },
    D: { point: { color: 'teal' } },
  },
};
